/**
 * GrowthChannelsCockpit
 *
 * Cockpit-only redesign of /growth-channels. Shows ALL marketing outlets
 * with Net CPA front-and-center on every row, fully reconciled to Lead
 * Sources and Lead Integrity. Classic mode is preserved untouched.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  summarize,
  classifyHealth,
  type ChannelRollup,
} from "@/lib/channelReconciliation";
import { useGrowthChannelRollups } from "@/hooks/useGrowthChannelsServerPaging";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  ArrowRight, ChevronDown, ChevronRight, Megaphone, Globe,
  ShieldCheck, AlertTriangle, Lock, Sparkles,
} from "lucide-react";

const PERIODS = [
  { id: "week", label: "Week" },
  { id: "month", label: "Month" },
  { id: "quarter", label: "Quarter" },
] as const;
type Period = typeof PERIODS[number]["id"];

const HEALTH_DOT: Record<string, string> = {
  healthy:          "bg-success",
  attention:        "bg-warning",
  underperforming:  "bg-destructive",
  paused:           "bg-muted-foreground/40",
  "no-data":        "bg-muted-foreground/30",
};

const HEALTH_TEXT: Record<string, string> = {
  healthy:          "text-success",
  attention:        "text-warning",
  underperforming:  "text-destructive",
  paused:           "text-muted-foreground",
  "no-data":        "text-muted-foreground",
};

const fmt$ = (n: number) => n >= 1000 ? `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : `$${n.toLocaleString()}`;
const fmtCpa = (n: number) => n > 0 ? `$${n.toLocaleString()}` : "—";
const fmtPct = (n: number) => `${Math.round(n)}%`;

function applyLocalOverrides(row: ChannelRollup, active: Record<string, boolean>, spend: Record<string, number>): ChannelRollup {
  const monthlySpend = spend[row.id] ?? row.monthlySpend;
  return {
    ...row,
    active: active[row.id] ?? row.active,
    monthlySpend,
    cpl: row.leads > 0 ? Math.round(monthlySpend / row.leads) : 0,
    cpb: row.appointments > 0 ? Math.round(monthlySpend / row.appointments) : 0,
    cpaGross: row.wonGross > 0 ? Math.round(monthlySpend / row.wonGross) : 0,
    cpaNet: row.wonNet > 0 ? Math.round(monthlySpend / row.wonNet) : 0,
    netConversionPct: row.leads > 0 ? (row.wonNet / row.leads) * 100 : 0,
    roi: monthlySpend > 0 ? ((row.revenue - monthlySpend) / monthlySpend) * 100 : 0,
  };
}

interface Props {
  /** When embedded inside MarketingPerformanceCockpit, suppress the outer
   *  page wrapper, h1, and intro copy — the parent owns the header. */
  embedded?: boolean;
}

export default function GrowthChannelsCockpit({ embedded = false }: Props = {}) {
  const serverRollups = useGrowthChannelRollups({ enabled: true });
  const [period, setPeriod] = useState<Period>("month");
  const [active, setActive] = useState<Record<string, boolean>>({});
  const [spend, setSpend] = useState<Record<string, number>>({});
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const rollups = useMemo(
    () => serverRollups.data.map((row) => applyLocalOverrides(row, active, spend)),
    [serverRollups.data, active, spend],
  );

  const summary = useMemo(() => summarize(rollups), [rollups]);

  const setChannelSpend = (id: string, value: number) =>
    setSpend(prev => ({ ...prev, [id]: value }));

  const toggleActive = (id: string) =>
    setActive(prev => ({ ...prev, [id]: !(prev[id] ?? true) }));

  const headerBlock = embedded ? (
    <div className="flex items-start justify-between gap-4 mb-5">
      <dl className="grid grid-cols-2 sm:grid-cols-5 gap-x-6 gap-y-3 flex-1">
        <Stat label="Spend"    value={fmt$(summary.totalSpend)} />
        <Stat label="Leads"    value={String(summary.totalLeads)} />
        <Stat label="Net jobs" value={String(summary.totalNetJobs)} />
        <Stat label="Net CPA"  value={fmtCpa(summary.blendedCpaNet)} emphasis sub={`${fmtCpa(summary.blendedCpl)} CPL`} />
        <Stat
          label="Channels active"
          value={`${summary.channelsActive} / ${summary.channelsTotal}`}
          sub={summary.unattributedLeads > 0
            ? `${summary.unattributedLeads} unattributed (${summary.unattributedPct}%)`
            : "All leads attributed"}
          subTone={summary.unattributedPct > 10 ? "warn" : undefined}
        />
      </dl>
      {/* When embedded inside MarketingPerformanceCockpit, the page-level
          Week/Month/Quarter toggle in the parent header owns the date range —
          do NOT render a second one here. */}
    </div>
  ) : (
    <header className="border-b border-border" style={{ paddingBottom: "var(--kdr-space-4)", marginBottom: "var(--kdr-space-5)" }}>
      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="kdr-display text-foreground">Leads</h1>
          <p className="kdr-meta mt-1.5 max-w-xl">
            Every marketing outlet — reconciled to lead sources, intake integrity, and revenue.
          </p>
        </div>
        <PeriodSwitch period={period} onChange={setPeriod} />
      </div>
      <dl className="grid grid-cols-2 sm:grid-cols-5 mt-6 -mb-1">
        <Stat label="Spend"          value={fmt$(summary.totalSpend)} />
        <Stat label="Leads"          value={String(summary.totalLeads)} />
        <Stat label="Net jobs"       value={String(summary.totalNetJobs)} />
        <Stat
          label="Net CPA"
          value={fmtCpa(summary.blendedCpaNet)}
          emphasis
          sub={`${fmtCpa(summary.blendedCpl)} CPL`}
        />
        <Stat
          label="Channels active"
          value={`${summary.channelsActive} / ${summary.channelsTotal}`}
          sub={summary.unattributedLeads > 0
            ? `${summary.unattributedLeads} unattributed (${summary.unattributedPct}%)`
            : "All leads attributed"}
          subTone={summary.unattributedPct > 10 ? "warn" : undefined}
        />
      </dl>
    </header>
  );

  const inner = (
    <>
      {headerBlock}

      {serverRollups.loading && (
        <p className="mb-3 text-[12px] text-muted-foreground">Loading server channel rollups...</p>
      )}
      {serverRollups.error && (
        <p className="mb-3 text-[12px] text-warning">Channel rollup view failed to load. Apply the growth-channel SQL migration and refresh.</p>
      )}

      {/* ─── CHANNELS TABLE ─── */}
      <section style={{ marginBottom: "var(--kdr-space-6)" }}>
        <SectionHeader
          eyebrow="Performance"
          title="All channels"
          aside={<Link to="/marketing-attribution" className="kdr-meta text-primary hover:underline inline-flex items-center gap-1">Campaign attribution <ArrowRight className="h-3 w-3" /></Link>}
        />

        {/* Column header */}
        <div className="grid grid-cols-[minmax(0,1.6fr)_70px_80px_80px_80px_90px_90px] items-center gap-3 px-4 py-2.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border">
          <span>Channel</span>
          <span className="text-right">Spend</span>
          <span className="text-right">Leads</span>
          <span className="text-right">Net jobs</span>
          <span className="text-right">CPL</span>
          <span className="text-right font-semibold text-foreground/80">Net CPA</span>
          <span className="text-right">Conv</span>
        </div>

        <ul className="divide-y divide-border border-b border-border">
          {(() => {
            if (rollups.length === 0) {
              return (
                <li className="px-4 py-8 text-center text-[13px] text-muted-foreground">
                  {serverRollups.loading ? "Loading channels..." : "No channel rollups found."}
                </li>
              );
            }
            // Cap "destructive" status density: if more than 1 channel classifies as
            // underperforming, only the WORST one (by Net CPA) keeps the destructive
            // hue — the rest are softened to "Needs attention". When everything is
            // red, nothing is.
            const underIds = rollups
              .filter(r => classifyHealth(r).status === "underperforming")
              .sort((a, b) => (b.cpaNet || 0) - (a.cpaNet || 0))
              .map(r => r.id);
            const worstId = underIds[0];
            return rollups.map(r => (
              <ChannelRow
                key={r.id}
                row={r}
                expanded={expandedId === r.id}
                onToggle={() => setExpandedId(expandedId === r.id ? null : r.id)}
                onActiveToggle={() => toggleActive(r.id)}
                onSpendChange={(v) => setChannelSpend(r.id, v)}
                softenStatus={underIds.includes(r.id) && r.id !== worstId}
              />
            ));
          })()}
        </ul>
      </section>

      {/* ─── RECONCILIATION ─── */}
      <Reconciliation rollups={rollups} summary={summary} />

      {/* ─── ATTRIBUTION FLOW ─── */}
      <section className="mt-8 px-1">
        <p className="kdr-meta uppercase tracking-wider font-semibold text-muted-foreground/70 mb-3">Attribution chain</p>
        <div className="flex items-center gap-2 flex-wrap text-[12px]">
          {["Channel", "Lead Source", "Lead", "Job", "Revenue"].map((step, i, arr) => (
            <span key={step} className="flex items-center gap-2">
              <span className={cn(
                "px-2.5 py-1 rounded-md border font-medium",
                i === 0 ? "bg-primary/8 text-primary border-primary/15" :
                i === arr.length - 1 ? "bg-success/8 text-success border-success/15" :
                "bg-muted text-foreground border-border",
              )}>{step}</span>
              {i < arr.length - 1 && <ArrowRight className="h-3 w-3 text-muted-foreground/40" />}
            </span>
          ))}
        </div>
      </section>
    </>
  );

  return embedded ? inner : <div className="max-w-6xl mx-auto pb-12 pt-2">{inner}</div>;
}

/* ────────────────────────────────────────────────────────────────────── */
/*  Channel row + expandable detail                                      */
/* ────────────────────────────────────────────────────────────────────── */

function ChannelRow({
  row, expanded, onToggle, onActiveToggle, onSpendChange, softenStatus = false,
}: {
  row: ChannelRollup;
  expanded: boolean;
  onToggle: () => void;
  onActiveToggle: () => void;
  onSpendChange: (n: number) => void;
  /** When true, demote a "underperforming" classification to "attention" so that
   *  destructive coloring is reserved for the single worst channel. */
  softenStatus?: boolean;
}) {
  const rawHealth = classifyHealth(row);
  const health = softenStatus && rawHealth.status === "underperforming"
    ? { status: "attention" as const, label: "Needs attention" }
    : rawHealth;
  const Icon = row.id === "unattributed" ? AlertTriangle
    : row.type === "managed" ? Lock
    : row.type === "tracked" ? Globe
    : row.type === "coming_soon" ? Sparkles
    : Megaphone;

  return (
    <li
      className={cn(
        "transition-colors",
        expanded && "bg-muted/30",
      )}
      data-status={health.status === "underperforming" ? "blocked" : health.status === "attention" ? "warn" : undefined}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full grid grid-cols-[minmax(0,1.6fr)_70px_80px_80px_80px_90px_90px] items-center gap-3 px-4 py-3.5 text-left hover:bg-muted/40 focus-visible:outline-none focus-visible:bg-muted/40"
      >
        {/* Channel name + status */}
        <div className="flex items-center gap-2.5 min-w-0">
          {expanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />}
          <Icon className={cn("h-3.5 w-3.5 shrink-0", row.id === "unattributed" ? "text-warning" : "text-muted-foreground")} />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[14px] font-semibold text-foreground truncate">{row.name}</span>
              <span className={cn("inline-flex items-center gap-1 text-[10px]", HEALTH_TEXT[health.status])}>
                <span className={cn("h-1.5 w-1.5 rounded-full", HEALTH_DOT[health.status])} />
                {health.label}
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground truncate mt-0.5">
              {row.type === "managed" && "Managed by Koredor · "}
              {row.type === "tracked" && "External · tracked · "}
              {row.type === "coming_soon" && "Coming soon · "}
              {row.id === "unattributed" && "Offline / unmapped · "}
              {row.sources.length > 0 ? row.sources.slice(0, 3).join(" · ") : "no lead sources mapped"}
              {row.sources.length > 3 && ` +${row.sources.length - 3}`}
            </p>
          </div>
        </div>

        <span className="text-right text-[13px] tabular-nums text-foreground">{row.monthlySpend > 0 ? fmt$(row.monthlySpend) : "—"}</span>
        <span className="text-right text-[13px] tabular-nums text-foreground">{row.leads || "—"}</span>
        <span className="text-right text-[13px] tabular-nums text-foreground">{row.wonNet || "—"}</span>
        <span className="text-right text-[13px] tabular-nums text-muted-foreground">{row.cpl > 0 ? fmt$(row.cpl) : "—"}</span>
        <span className={cn(
          "text-right text-[15px] font-semibold tabular-nums",
          row.cpaNet === 0 ? "text-muted-foreground/40" :
          row.cpaNet > 500 ? "text-destructive" :
          row.cpaNet > 350 ? "text-warning" :
          "text-foreground",
        )}>{fmtCpa(row.cpaNet)}</span>
        <span className="text-right text-[13px] tabular-nums text-muted-foreground">
          {row.leads > 0 ? fmtPct(row.netConversionPct) : "—"}
        </span>
      </button>

      {expanded && (
        <ChannelDetail
          row={row}
          onActiveToggle={onActiveToggle}
          onSpendChange={onSpendChange}
        />
      )}
    </li>
  );
}

function ChannelDetail({
  row, onActiveToggle, onSpendChange,
}: {
  row: ChannelRollup;
  onActiveToggle: () => void;
  onSpendChange: (n: number) => void;
}) {
  const showControls = row.type !== "derived" && row.type !== "coming_soon";
  // Deep-link works in both layouts: merged uses tab=lead-sources&pivot=sources;
  // legacy uses tab=sources. The cockpit honors both.
  const sourcesHref = `/growth-channels?tab=lead-sources&pivot=sources&channel=${row.id}`;
  return (
    <div className="px-4 pb-5 pt-1 space-y-4">

      {/* Funnel */}
      <div className="grid grid-cols-5 gap-x-2 gap-y-3 text-[12px]">
        {[
          { label: "Leads", value: row.leads },
          { label: "Appointments", value: row.appointments },
          { label: "Won (gross)", value: row.wonGross },
          { label: "Net jobs", value: row.wonNet, emphasis: true },
          { label: "Pending", value: row.pending, tone: row.pending > 0 ? "warn" : undefined },
        ].map((s, i, arr) => (
          <div key={s.label} className={cn("relative", i < arr.length - 1 && "pr-2")}>
            <p className={cn(
              "text-[18px] font-semibold tabular-nums leading-none",
              s.emphasis && "text-success",
              s.tone === "warn" && "text-warning",
            )}>{s.value || "0"}</p>
            <p className="text-[11px] text-muted-foreground mt-1.5">{s.label}</p>
            {i < arr.length - 1 && (
              <span className="absolute right-0 top-1 text-muted-foreground/30 text-[12px]">→</span>
            )}
          </div>
        ))}
      </div>

      {/* Spend economics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3 pt-3 border-t border-border">
        <DetailMetric label="Cost / Lead"    value={fmt$(row.cpl)} />
        <DetailMetric label="Cost / Booking" value={fmt$(row.cpb)} />
        <DetailMetric label="CPA (gross)"    value={fmtCpa(row.cpaGross)} />
        <DetailMetric label="CPA (net)"      value={fmtCpa(row.cpaNet)} emphasis />
      </div>

      {/* Insight strip */}
      <ChannelInsight row={row} />

      {/* Footer: spend controls inline + sources deep link */}
      <div className="flex items-center justify-between gap-4 flex-wrap pt-3 border-t border-border">
        {showControls ? (
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-[12px] text-muted-foreground">
              <Switch checked={row.active} onCheckedChange={onActiveToggle} />
              <span className="font-medium text-foreground">Active</span>
            </label>
            <div className="flex items-center gap-1.5">
              <label className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground">Monthly spend</label>
              <span className="text-muted-foreground text-[13px]">$</span>
              <Input
                type="number"
                value={row.monthlySpend || ""}
                onChange={e => onSpendChange(Number(e.target.value) || 0)}
                className="h-7 w-24 text-[13px]"
                placeholder={String(DEFAULT_CHANNEL_SPEND[row.id] ?? 0)}
              />
            </div>
          </div>
        ) : <span />}

        <Link
          to={sourcesHref}
          className="text-[12px] text-primary hover:underline inline-flex items-center gap-1"
        >
          {row.sources.length > 0
            ? `View ${row.sources.length} source${row.sources.length === 1 ? "" : "s"} & quality`
            : "Map lead sources"}
          <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
}

function ChannelInsight({ row }: { row: ChannelRollup }) {
  const health = classifyHealth(row);
  let message = "Performance is within expected range.";
  let tone: "good" | "warn" | "bad" | "neutral" = "neutral";

  if (health.status === "no-data") {
    message = "No leads received this period. Verify the channel is sending traffic.";
    tone = "neutral";
  } else if (health.status === "paused") {
    message = "Channel is paused. Toggle Active to resume tracking.";
    tone = "neutral";
  } else if (row.cpaNet > 500) {
    message = `Net CPA of ${fmtCpa(row.cpaNet)} is well above target — review targeting or pause spend.`;
    tone = "bad";
  } else if (row.cpaNet > 350) {
    message = `Net CPA trending high (${fmtCpa(row.cpaNet)}). Monitor for another cycle before reducing budget.`;
    tone = "warn";
  } else if (row.netConversionPct >= 25 && row.cpaNet > 0 && row.cpaNet <= 300) {
    message = `Strong net conversion (${fmtPct(row.netConversionPct)}) at ${fmtCpa(row.cpaNet)} CPA — consider scaling budget.`;
    tone = "good";
  } else if (row.outOfAreaPct > 15) {
    message = `${fmtPct(row.outOfAreaPct)} of leads are out-of-area. Tighten geo targeting with the partner.`;
    tone = "warn";
  } else if (row.validLeadPct > 0 && row.validLeadPct < 60) {
    message = `Lead quality at ${fmtPct(row.validLeadPct)} is below the 60% benchmark.`;
    tone = "warn";
  }

  const toneClass =
    tone === "good" ? "border-success/20 bg-success/5 text-success" :
    tone === "warn" ? "border-warning/20 bg-warning/5 text-warning" :
    tone === "bad"  ? "border-destructive/20 bg-destructive/5 text-destructive" :
    "border-border bg-muted/30 text-muted-foreground";

  return (
    <div className={cn("flex items-start gap-2 px-3 py-2 rounded-md border", toneClass)}>
      <span className={cn("h-1.5 w-1.5 rounded-full mt-1.5 shrink-0",
        tone === "good" ? "bg-success" : tone === "warn" ? "bg-warning" : tone === "bad" ? "bg-destructive" : "bg-muted-foreground/40")} />
      <p className="text-[12px] leading-snug">{message}</p>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/*  Reconciliation panel                                                 */
/* ────────────────────────────────────────────────────────────────────── */

function Reconciliation({ rollups, summary }: { rollups: ChannelRollup[]; summary: ReturnType<typeof summarize> }) {
  const totalSources = rollups.reduce((s, r) => s + r.sources.length, 0);
  const orphanLeads = summary.unattributedLeads;
  const integrityIntake = rollups.reduce((s, r) => s + r.intakeCount, 0);
  const approvedIntake = Math.round(
    rollups.reduce((s, r) => s + (r.intakeCount * (r.validLeadPct / 100)), 0),
  );
  const approvedPct = integrityIntake > 0 ? Math.round((approvedIntake / integrityIntake) * 100) : 0;

  const checks = [
    {
      ok: orphanLeads === 0,
      label: "Lead source attribution",
      detail: orphanLeads === 0
        ? `All ${summary.totalLeads} leads attributed to a channel.`
        : `${orphanLeads} of ${summary.totalLeads} leads have no channel mapping (${summary.unattributedPct}%).`,
      cta: { label: "Review in Lead Sources", to: "/lead-sources" },
    },
    {
      ok: true,
      label: "Lead Integrity intake",
      detail: integrityIntake > 0
        ? `${approvedIntake} of ${integrityIntake} intake records approved (${approvedPct}%).`
        : "No intake records are represented in the current channel rollup.",
      cta: { label: "Open Lead Integrity", to: "/lead-integrity" },
    },
    {
      ok: true,
      label: "Source coverage",
      detail: `${totalSources} lead sources mapped across ${summary.channelsTotal} channels.`,
      cta: { label: "Edit mapping", to: "/lead-sources" },
    },
  ];

  return (
    <section style={{ marginBottom: "var(--kdr-space-5)" }}>
      <SectionHeader
        eyebrow="Integrity"
        title="Reconciliation"
        aside={<Link to="/lead-integrity" className="kdr-meta text-primary hover:underline inline-flex items-center gap-1">Lead Integrity <ArrowRight className="h-3 w-3" /></Link>}
      />
      <ul className="divide-y divide-border border-y border-border">
        {checks.map(c => (
          <li key={c.label} className="flex items-center gap-4 px-4 py-3.5">
            <ShieldCheck className={cn("h-4 w-4 shrink-0", c.ok ? "text-success" : "text-warning")} />
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium text-foreground">{c.label}</p>
              <p className="text-[12px] text-muted-foreground mt-0.5">{c.detail}</p>
            </div>
            <Button variant="ghost" size="sm" asChild className="h-7 text-[11px] gap-1">
              <Link to={c.cta.to}>{c.cta.label} <ArrowRight className="h-3 w-3" /></Link>
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/*  Primitives                                                           */
/* ────────────────────────────────────────────────────────────────────── */

function PeriodSwitch({ period, onChange }: { period: Period; onChange: (p: Period) => void }) {
  return (
    <div role="tablist" className="inline-flex rounded-md border border-border bg-card p-0.5">
      {PERIODS.map(p => (
        <button
          key={p.id}
          role="tab"
          aria-selected={period === p.id}
          onClick={() => onChange(p.id)}
          className={cn(
            "px-3 py-1 text-[12px] font-medium rounded-[6px] transition-colors",
            period === p.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
          )}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

function Stat({ label, value, sub, subTone, emphasis }: {
  label: string; value: string; sub?: string; subTone?: "warn"; emphasis?: boolean;
}) {
  return (
    <div className="flex flex-col">
      <dt className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground">{label}</dt>
      <dd className={cn(
        "tabular-nums tracking-tight mt-1.5",
        emphasis ? "text-[26px] font-semibold text-foreground" : "text-[22px] font-semibold text-foreground",
      )}>{value}</dd>
      {sub && (
        <span className={cn("text-[11px] mt-0.5", subTone === "warn" ? "text-warning" : "text-muted-foreground")}>{sub}</span>
      )}
    </div>
  );
}

function SectionHeader({ eyebrow, title, aside }: { eyebrow?: string; title: string; aside?: React.ReactNode }) {
  return (
    <div className="flex items-end justify-between gap-3 mb-3">
      <div>
        {eyebrow && <p className="text-[10px] uppercase tracking-[0.08em] font-semibold text-muted-foreground/70">{eyebrow}</p>}
        <h2 className="kdr-section text-foreground mt-0.5">{title}</h2>
      </div>
      {aside}
    </div>
  );
}

function DetailMetric({ label, value, sub, emphasis, tone }: {
  label: string; value: string; sub?: string; emphasis?: boolean; tone?: "warn";
}) {
  return (
    <div>
      <p className={cn(
        "tabular-nums leading-none",
        emphasis ? "text-[16px] font-semibold text-success" :
        tone === "warn" ? "text-[15px] font-semibold text-warning" :
        "text-[15px] font-semibold text-foreground",
      )}>{value}</p>
      <p className="text-[11px] text-muted-foreground mt-1.5">{label}</p>
      {sub && <p className="text-[10px] text-muted-foreground/70 mt-0.5">{sub}</p>}
    </div>
  );
}
